import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  CheckCircle,
  XCircle,
  Mail,
  Building2,
  Users,
  Calendar,
  Clock,
  UserPlus,
} from "lucide-react";
import { InviteService } from "../../services/inviteService";

const PendingInvitesCheck = ({
  userEmail,
  onInvitesAccepted,
  onInviteCheckComplete,
}) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);
  const [error, setError] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [title, setTitle] = useState("");
  const [checkComplete, setCheckComplete] = useState(false);

  useEffect(() => {
    if (userEmail) {
      checkPendingInvites();
    } else {
      // If no email, mark check as complete immediately
      handleCheckComplete();
    }
  }, [userEmail]);

  // Handle when invite check is complete (with or without invites)
  const handleCheckComplete = () => {
    if (!checkComplete) {
      setCheckComplete(true);
      if (onInviteCheckComplete) {
        onInviteCheckComplete();
      }
    }
  };

  const checkPendingInvites = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await InviteService.checkInvitesByEmail(userEmail);

      if (response.invites && response.invites.length > 0) {
        setPendingInvites(response.invites);
        setShowDialog(true);
        // Don't call handleCheckComplete yet - wait for user to handle invites
      } else {
        // No invites found, mark check as complete immediately
        handleCheckComplete();
      }
    } catch (err) {
      console.error("❌ STEP 3C: Error checking pending invites:", err);
      setError(err.message);
      // Even if error, mark check as complete so app can proceed
      handleCheckComplete();
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (invite) => {
    try {
      setAccepting(invite.id);
      setError(null);

      const acceptanceData = {
        auth0_user: {
          sub: user.sub,
          email: user.email,
          name: user.name,
        },
        ...(title.trim() && { title: title.trim() }),
      };

      const response = await InviteService.acceptInvite(
        invite.id,
        acceptanceData,
        getAccessTokenSilently
      );

      // Remove accepted invite from list
      setPendingInvites((prev) => prev.filter((inv) => inv.id !== invite.id));

      // If no more invites, close dialog and notify parent
      if (pendingInvites.length === 1) {
        setShowDialog(false);
        if (onInvitesAccepted) {
          onInvitesAccepted(response);
        }
        // All invites handled, mark check as complete
        handleCheckComplete();
      }
    } catch (err) {
      console.error("Error accepting invite:", err);
      setError(err.message);
    } finally {
      setAccepting(null);
    }
  };

  const handleDeclineInvite = (invite) => {
    // Remove from list without accepting
    setPendingInvites((prev) => prev.filter((inv) => inv.id !== invite.id));

    // If no more invites, close dialog and complete check
    if (pendingInvites.length === 1) {
      setShowDialog(false);
      handleCheckComplete();
    }
  };

  const formatRole = (role) => {
    const roleMap = {
      org_admin: "Organization Admin",
      business_unit_head: "Business Unit Head",
      business_user: "Business User",
    };
    return roleMap[role] || role;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  // Don't render anything if loading and no dialog to show
  if (loading && !showDialog) {
    return null;
  }

  // Don't render anything if no invites and check is complete
  if (pendingInvites.length === 0 && !showDialog) {
    return null;
  }

  return (
    <Dialog open={showDialog} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            You Have Pending Invitations!
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            You have {pendingInvites.length} pending invitation
            {pendingInvites.length !== 1 ? "s" : ""} waiting for your response.
          </p>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto max-h-[60vh]">
          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              {error}
            </Alert>
          )}

          {/* Job Title Input (shown once for all invites) */}
          <div className="space-y-2">
            <Label htmlFor="title">Job Title (Optional)</Label>
            <Input
              id="title"
              placeholder="e.g., Software Engineer"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              This will be applied to your profile for all accepted invitations
            </p>
          </div>

          {/* Pending Invites */}
          <div className="space-y-4">
            {pendingInvites.map((invite) => {
              const expired = isExpired(invite.expires_at);

              return (
                <Card
                  key={invite.id}
                  className={`${expired ? "opacity-75" : ""}`}
                >
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      {/* Invite Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback>
                              {invite.invited_name
                                ? invite.invited_name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                : invite.email.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">
                                {invite.organization.name}
                              </p>
                              <Badge
                                variant={expired ? "destructive" : "default"}
                              >
                                {formatRole(invite.role)}
                              </Badge>
                            </div>
                            {invite.business_unit && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Building2 className="h-3 w-3" />
                                {invite.business_unit.name}
                              </div>
                            )}
                          </div>
                        </div>

                        {expired && (
                          <Badge variant="destructive" className="text-xs">
                            <XCircle className="h-3 w-3 mr-1" />
                            Expired
                          </Badge>
                        )}
                      </div>

                      {/* Invite Details */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>
                            Invited by: {invite.invited_by?.name || "Unknown"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span>Sent: {formatDate(invite.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className={expired ? "text-red-600" : ""}>
                            {expired ? "Expired" : "Expires"}:{" "}
                            {formatDate(invite.expires_at)}
                          </span>
                        </div>
                      </div>

                      {/* Personal Message */}
                      {invite.message && (
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-sm italic">"{invite.message}"</p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => handleDeclineInvite(invite)}
                          disabled={accepting === invite.id}
                        >
                          Decline
                        </Button>
                        <Button
                          onClick={() => handleAcceptInvite(invite)}
                          disabled={accepting === invite.id || expired}
                          className="flex items-center gap-2"
                        >
                          {accepting === invite.id ? (
                            <>
                              <Spinner size="sm" />
                              Accepting...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4" />
                              Accept Invitation
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {pendingInvites.length} invitation
            {pendingInvites.length !== 1 ? "s" : ""} remaining
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setShowDialog(false);
              handleCheckComplete();
            }}
          >
            I'll Decide Later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PendingInvitesCheck;
